import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  AutocompleteItem,
  ProgressBar,
  Utils,
} from 'mw-style-react';
import { GET_FORM_ITEM_OPTIONS } from '@control-front-end/common/constants/forms';
import useOutsideClick from 'useOutsideClick';
import './AutocompleteSources.scss';

/**
 * Компонент для работы с источниками данных
 */
function AutocompleteSources(props) {
  const {
    formId,
    id,
    value,
    loadInitValue = false,
    error,
    helperText,
    visibility,
    label,
    className,
    getFormData,
    onChange,
    styleType = 'default',
    portal = true,
    renderChipsItems = () => {},
  } = props;
  const dispatch = useDispatch();
  const searchBox = useRef();
  const [query, setQuery] = useState('');
  const [focus, setFocus] = useState(false);
  const [valueTitle, setValueTitle] = useState(value);
  const initState = {
    list: [],
    limit: 5,
    offset: 0,
    endList: false,
  };
  const [options, setOptions] = useState(initState);
  const [isLoaded, setLoading] = useState(true);
  const [initFromSource, setSourceInit] = useState(loadInitValue);

  /**
   * Получение элементов выбора
   */
  const getOptions = (loadMore = false) => {
    if (!formId || (loadMore && options.endList)) return;
    setLoading(false);
    dispatch({
      type: GET_FORM_ITEM_OPTIONS.REQUEST,
      payload: {
        formId,
        formData: getFormData(),
        itemId: id,
        loadMore,
        ...options,
      },
      callback: (data) => {
        if (initFromSource && value && data.list && data.list.length > 0) {
          const showItem = data.list.find((item) => item.value === value);
          if (showItem) {
            setValueTitle([showItem]);
          }
        }
        setOptions({ ...options, ...data });
        setLoading(true);
      },
    });
  };

  /**
   * Обработка снятия фокуса
   */
  const handleBlur = () => {
    const newOffset = className === 'multiSelect' ? value.length : 0;
    setFocus(false);
    setQuery('');
    setOptions({ ...initState, offset: newOffset });
  };

  /**
   * Обработка фокуса
   */
  const handleFocus = () => {
    setFocus(true);
    getOptions();
  };

  useOutsideClick({ ref: searchBox, callback: handleBlur });

  const handleScroll = Utils.debounce((e) => {
    if (query.length) return;
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    const scrollBottom = scrollHeight - (scrollTop + clientHeight);
    if (scrollBottom >= 500) return;
    if (!options.q) getOptions(true);
  }, 200);

  /**
   * Обработка поискового запроса
   * @param q
   */
  const handleSearch = ({ value: q }) => {
    setQuery(q);
    if (!q.length) {
      setOptions(initState);
      getOptions();
      return;
    }
    if (!formId || q.length < 2) return;
    dispatch({
      type: GET_FORM_ITEM_OPTIONS.REQUEST,
      payload: { formId, formData: getFormData(), itemId: id, q, offset: 0 },
      callback: (data) => setOptions({ ...initState, ...data }),
    });
  };

  /**
   * Обработка клика по найденному варианту
   * @param item
   */
  const handleItemClick = (item) => {
    if (className === 'multiSelect') {
      const newItems = value.slice(0);
      newItems.push(item);
      onChange({ id, value: newItems, error: false });
    } else {
      onChange({ id, value: [item], error: false });
      setValueTitle([item]);
    }
    handleBlur();
  };

  const renderSearchResult = () => {
    if (!focus) return;
    const selected = className === 'multiSelect' ? value : [];
    const list = options.list.filter(
      (item) => selected.findIndex((i) => i.value === item.value) === -1
    );
    const items = list.map((i) => (
      <AutocompleteItem
        key={i.value}
        id={i.value}
        size="small"
        value={i.title}
        onClick={() => handleItemClick(i)}
      />
    ));
    if (!query.length && !items.length) return;
    return (
      <div>
        {items.length ? (
          items
        ) : (
          <AutocompleteItem
            size="small"
            value="Not found"
            onClick={handleBlur}
          />
        )}
      </div>
    );
  };
  const getValueTitle = () => {
    if (className === 'multiSelect') return query;
    return valueTitle && valueTitle.length ? valueTitle[0].title : '';
  };
  if (initFromSource && value) {
    getOptions();
    setSourceInit(false);
  }
  return (
    <div
      ref={searchBox}
      styleName="field"
      onMouseOver={(e) => e.stopPropagation()}
    >
      <Autocomplete
        styleName={styleType}
        label={label}
        value={focus ? query : getValueTitle()}
        bordered={true}
        error={error}
        helperText={helperText}
        onChange={handleSearch}
        onBlur={handleBlur}
        onFocus={handleFocus}
        visibility={visibility}
        searchResult={renderSearchResult}
        chipsItems={renderChipsItems}
        portalOptions={
          portal
            ? {
                node: searchBox.current,
                offsetY: 20,
                offsetX: 4,
                onScroll: handleScroll,
                vAlign: 'bottom',
                height: '186px',
              }
            : null
        }
      />
      {!isLoaded ? (
        <div styleName="loader">
          <ProgressBar size="small" />
        </div>
      ) : null}
    </div>
  );
}

AutocompleteSources.propTypes = {
  id: PropTypes.string,
  formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  loadInitValue: PropTypes.bool,
  styleType: PropTypes.oneOf(['default', 'small']),
  className: PropTypes.string,
  label: PropTypes.string,
  error: PropTypes.bool,
  visibility: PropTypes.string,
  helperText: PropTypes.string,
  portal: PropTypes.bool,
  onChange: PropTypes.func,
  getFormData: PropTypes.func,
  renderChipsItems: PropTypes.func,
};

export default AutocompleteSources;
